import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageTitle from "components/layout/PageTitle";
import Footer from "components/layout/Footer";
import { fillParent, scrollingBox } from "utils/base-styles";
import { SUBMISSION_FOLDERS_UPDATED_EVENT, useTeacherSubmissionFolders } from "../hooks";
import { createSubmissionFolder } from "../services";
import SubmissionFolderDialog from "./SubmissionFolderDialog";

/**
 * Landing route for /collections/submissions. Folder navigation lives in the
 * sidebar, so this route only opens the create dialog and hands off to the
 * first active folder's detail view.
 */
export default function SubmissionFoldersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { folders, loading, error, refresh } = useTeacherSubmissionFolders();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (location.state?.openCreate) {
      setDialogOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  const activeFolders = useMemo(
    () => folders.filter((folder) => !folder.isArchived_ysn),
    [folders]
  );

  useEffect(() => {
    if (!loading && !dialogOpen && !location.state?.openCreate && activeFolders.length > 0) {
      navigate(`/collections/submissions/${activeFolders[0].submissionFolderID_ids}`, {
        replace: true,
      });
    }
  }, [activeFolders, dialogOpen, loading, location.state, navigate]);

  const saveFolder = async (payload) => {
    setSaving(true);
    try {
      const created = await createSubmissionFolder(payload);
      toast.success("Submission folder created.");
      setDialogOpen(false);

      const refreshedFolders = await refresh();
      window.dispatchEvent(new Event(SUBMISSION_FOLDERS_UPDATED_EVENT));

      const createdFolderID =
        created?.submissionFolderID_ids ??
        created?.submissionFolderID ??
        (Array.isArray(refreshedFolders)
          ? [...refreshedFolders]
              .reverse()
              .find(
                (folder) =>
                  folder.folderName_chr === payload.folderName &&
                  Number(folder.distributionListID) === Number(payload.distributionListID)
              )?.submissionFolderID_ids
          : null);

      if (createdFolderID != null) {
        navigate(`/collections/submissions/${createdFolderID}`, { replace: true });
      }
    } catch (requestError) {
      toast.error(requestError.message || "Unable to save submission folder.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ ...fillParent, ...scrollingBox, px: { xs: 2, md: 4 } }}>
      <Helmet><title>Sooth - Submissions</title></Helmet>
      <Stack gap={3} pb={8}>
        <PageTitle sx={{ mt: 3 }}>Submissions</PageTitle>
        {error && <Alert severity="error">Unable to load submission folders.</Alert>}
        {loading && <Typography color="text.secondary">Loading submission folders…</Typography>}
        {!loading && !error && activeFolders.length === 0 && (
          <Stack gap={2} alignItems="flex-start">
            <Alert severity="info">
              No submission folders yet. Create one to start collecting student work.
            </Alert>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              New Submission Folder
            </Button>
          </Stack>
        )}
      </Stack>
      <Footer sx={{ px: 0 }} />
      <SubmissionFolderDialog
        open={dialogOpen}
        folder={null}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={saveFolder}
      />
    </Box>
  );
}
